// @ts-check
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import dayjs from 'dayjs';
import { validateEmail, validatePhone } from '../../../util/validation';
import Countries from '../../../util/countries';
import CountryPicker from '../../country-select';
import ErrorPopup from '../../error';
import AgeGage from '../../age-cage';

export default class Presenter {
  constructor(model, config, onRegister) {
    this.model = model;
    this.facebookAppId = config.facebook_app_id;
    this.googleAppId = config.google_app_id;
    this.outsideAppId = config.outside_app_id;
    this.oauthOnly = config.oauth_only;
    this.advertiserName = config.advertiser_name;
    this.config = config.claim;
    this.supported_locales = config.supported_locales;
    this.age = config.age;
    this.token = config.token;
    this.errorConfig = config.error || {};
    this.onRegister = onRegister;
  }

  async onAttach() {
    const { allow_phone: allowPhone, allow_email: allowEmail, regions } = this.token;
    const {
      consent,
      capture_additional: captureAdditional,
      capture_name: captureName,
      name_type: nameType,
      custom_input: customInput,
    } = this.config;

    if (this.age && this.age.min) {
      AgeGage.show(
        this.age.format,
        this.age.min,
        this.age.redirect,
        this.age.delay || 5000,
        (date) => {
          this.dateOfBirth = dayjs(date).format('YYYY-MM-DD');
        }
      );
    }

    const baseLanguage = navigator.language.split('-')[0];

    // Use the browser's language, otherwise use the base language (e.g. 'en' for 'en-GB'), otherwise fallback onto the config's default language, otherwise 'en'.
    if (this.supported_locales?.includes(navigator.language)) {
      this.language = navigator.language;
    } else if (this.supported_locales?.includes(baseLanguage)) {
      this.language = baseLanguage;
    } else {
      const defaultLanguage = this.config.default_language;
      let defaultLocale = 'en';
      if (typeof defaultLanguage === 'string') {
        defaultLocale = defaultLanguage;
      } else if (Array.isArray(defaultLanguage) && defaultLanguage.length > 0) {
        [defaultLocale] = defaultLanguage;
      }
      this.language = defaultLocale;
    }

    this.model.allowPhone = allowPhone;
    this.model.allowEmail = allowEmail;
    this.model.captureName = captureName;
    this.model.nameType = nameType;
    this.model.captureAdditional = captureAdditional;
    this.model.customInput = customInput;
    this.model.showFacebook = !!this.facebookAppId;
    this.model.showGoogle = !!this.googleAppId;
    this.model.showOutsideOauth = !!this.outsideAppId;
    this.model.oauthOnly = !!this.oauthOnly;
    this.model.advertiserName = this.advertiserName;

    const { marketing: marketingConsent, custom: customConsent, default: defaultConsent } = consent;

    this.model.showMarketingConsent = marketingConsent?.enabled ?? false;
    this.model.marketingConsentTerms = marketingConsent?.terms ?? '';
    this.model.showCustomConsent = customConsent?.enabled ?? false;
    this.model.showConsent = defaultConsent?.enabled ?? true;

    let defaultCountry = {
      code: 'us',
      phonePrefix: '+1',
      name: 'United States',
    };
    if (regions?.length > 0) {
      const result = (await Countries.get().catch(() => [])).find(
        (country) => country.code.toLowerCase() === regions[0].toLowerCase()
      );
      if (result) {
        defaultCountry = result;
      }
    }

    let detected = (await Countries.detect().catch(() => {})) || defaultCountry;
    if (!regions.find((country) => detected.code.toLowerCase() === country.toLowerCase())) {
      detected = defaultCountry;
    }
    if (regions?.length === 1) {
      this.model.disableCountryPicker = true;
    }
    this.selectedCountry = detected;
    this.model.countryCode = this.selectedCountry.phonePrefix;
  }

  onInitialTokenChange(initialToken) {
    this.onTokenChange(initialToken);
  }

  onTokenChange(value) {
    this.model.tokenValue = value;
    const { allow_phone: allowPhone } = this.token;
    this.model.inputValue = value;
    if (allowPhone && validatePhone(value) && value.indexOf('+') !== 0) {
      this.model.showCountryCode = true;
    } else {
      this.model.showCountryCode = false;
    }
  }

  onCustomInputChange(value, custom) {
    // eslint-disable-next-line no-param-reassign
    custom.value = value;
    // eslint-disable-next-line no-self-assign
    this.model.customInput = this.model.customInput;
  }

  onNameChange(value) {
    this.model.nameValue = value;
  }

  onFacebookClick() {
    this.onNextClick('facebook');
  }

  onGoogleClick() {
    this.onNextClick('google');
  }

  onOutsideClick() {
    this.onNextClick('outside');
  }

  onNextClick(continueWith) {
    const {
      nameValue,
      primaryConsent,
      customConsent,
      selectedAdditional,
      marketingConsent,
      showMarketingConsent,
      customInput,
    } = this.model;
    let { tokenValue } = this.model;
    tokenValue = tokenValue?.trim();
    const { allow_phone: allowPhone, allow_email: allowEmail } = this.token;

    const {
      consent,
      capture_additional: captureAdditional,
      capture_name: captureName,
    } = this.config;

    const { delay: errorDelay, redirect_on_go: redirectOnGoError } = this.errorConfig;

    let hasErrors = false;

    if (captureAdditional) {
      if (!selectedAdditional) {
        hasErrors = true;
        this.model.errorAdditional = 'claim.landing.additional.error_select';
      } else this.model.errorAdditional = null;
    }

    if (consent?.custom?.required === true) {
      if (!customConsent) {
        hasErrors = true;
        this.model.errorCustomConsent = 'claim.landing.error_custom_consent';
      } else this.model.errorCustomConsent = null;
    }

    if (continueWith !== 'facebook' && continueWith !== 'google' && continueWith !== 'outside') {
      if (allowPhone && allowEmail) {
        if (!validateEmail(tokenValue) && !validatePhone(tokenValue)) {
          this.model.errorToken = 'claim.landing.error_invalid_email_phone_number';
          hasErrors = true;
        } else this.model.errorToken = null;
      } else if (allowEmail) {
        if (!validateEmail(tokenValue)) {
          this.model.errorToken = 'claim.landing.error_invalid_email';
          hasErrors = true;
        } else this.model.errorToken = null;
      } else if (allowPhone) {
        if (!validatePhone(tokenValue)) {
          this.model.errorToken = 'claim.landing.error_invalid_phone_number';
          hasErrors = true;
        } else this.model.errorToken = null;
      }

      if (allowPhone && validatePhone(tokenValue)) {
        try {
          const phoneUtil = PhoneNumberUtil.getInstance();
          const token = phoneUtil
            .format(phoneUtil.parse(tokenValue, this.selectedCountry.code), PhoneNumberFormat.E164)
            .trim();
          if (!token.startsWith(this.selectedCountry.phonePrefix)) {
            this.model.errorToken = 'claim.landing.error_invalid_region';
            hasErrors = true;
          } else if (token.length > 0) {
            tokenValue = token;
            this.model.errorToken = null;
          }
        } catch (error) {
          console.log(error);
          this.model.errorToken = 'claim.landing.error_invalid_phone_number';
          hasErrors = true;
        }
      }
      if (captureName) {
        if (nameValue?.length === 0) {
          this.model.errorName = 'claim.landing.error_invalid_name';
          hasErrors = true;
        } else this.model.errorName = null;
      }
    } else {
      this.model.errorToken = null;
      this.model.errorName = null;
    }
    if (!primaryConsent) {
      this.model.errorPrimaryConsent = 'claim.landing.error_consent';
      hasErrors = true;
    } else {
      this.model.errorPrimaryConsent = null;
    }

    customInput?.forEach((input) => {
      if (input.required && !input.value) {
        hasErrors = true;
        // eslint-disable-next-line no-param-reassign
        input.error = input.required;
      } else {
        // eslint-disable-next-line no-param-reassign
        input.error = '';
      }
    });
    if (!hasErrors) {
      this.model.errorPhone = null;
      this.model.errorEmail = null;
      this.model.errorName = null;
      this.model.loading = true;

      const params = { user: tokenValue, consent: 1 };

      if (nameValue?.length > 0) {
        params.name = nameValue;
      }

      if (selectedAdditional) {
        params.select = selectedAdditional;
      }

      if (showMarketingConsent) {
        params.optin = marketingConsent ? 'yes' : 'no';
      }

      if (this.dateOfBirth) {
        params.birthday = this.dateOfBirth;
      }

      customInput?.forEach((input, index) => {
        params[`input_${index + 1}`] = input.value;
      });

      if (continueWith === 'facebook') {
        params.connect = 'facebook';
      } else if (continueWith === 'google') {
        params.connect = 'google';
      } else if (continueWith === 'outside') {
        params.connect = 'outside';
      }

      if (this.language) {
        params.language = this.language;
      }

      VlabsUser.go(
        '/signup',
        params,
        (_, notificationType) => {
          this.onRegister?.(notificationType);
        },
        (error) => {
          ErrorPopup.show(error, () => {}, redirectOnGoError && errorDelay);
          console.log(error);
        }
      );
    }
  }

  onConsentToggle(consent) {
    this.model.primaryConsent = consent;
  }

  onMarketingConsentToggle(consent) {
    this.model.marketingConsent = consent;
  }

  onCustomConsentToggle(consent) {
    this.model.customConsent = consent;
  }

  onCountryCodeClick() {
    const { regions } = this.token;
    CountryPicker.show(this.selectedCountry, regions, null, (selected) => {
      if (selected) {
        this.selectedCountry = selected;
        this.model.countryCode = this.selectedCountry.phonePrefix;
      }
    });
  }

  onAdditionalSelect(value) {
    this.model.selectedAdditional = value;
  }
}
